/**
 * Schema of CargaCsvSimplesSemQtdFotoObservacaoColeta
 * @author Thacryba
 */
import {$, cep, phone, bool, cpfOrCnpj, datetime, InputType, Schema, cnpj} from 'simpli-web-sdk'
import * as Helpers from '@/helpers'
import moment from 'moment'
import {DeliveryStatus} from '@/enums/DeliveryStatus'
import CargaCsvSimplesSemQtdFotoObservacaoColeta from '@/model/resource/CargaCsvSimplesSemQtdFotoObservacaoColeta'
import RenderCargaDanfe from '@/components/render/carga/RenderCargaDanfe.vue'
import RenderLabelValue from '@/components/render/RenderLabelValue.vue'
import RenderNumber from '@/components/render/RenderNumber.vue'
import {datetimeForCsv} from '@/helpers'
import RenderCargaBarcode from '@/components/render/carga/RenderCargaBarcode.vue'

export default (model: CargaCsvSimplesSemQtdFotoObservacaoColeta): Schema => ({
  idCargaPk: {
    content: model.idCargaPk,
    textContent: model.idCargaPk,
    editable: false,
  },

  type: {
    content: model.deliveryType,
    textContent: model.deliveryType,
  },

  possuiDanfe: {
    content: bool(model.possuiDanfe),
    input: InputType.CHECKBOX,
    hidden: true,
  },

  nroNotaFiscalAlt: {
    content: Helpers.formatScientificNumber(model.nroNotaFiscal),
  },

  nroNotaFiscal: {
    content: [
      model.nroNotaFiscal,
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('nroNotaFiscal'),
          value: model.nroNotaFiscal,
        },
      },
      model.nroNotaFiscal,
    ],
    textContent: null,
    input: InputType.TEXT,
    meta: {
      maxlength: '255',
    },
  },

  serialNumber: {
    content: [
      model.serialNumber,
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('serialNumber'),
          value: model.serialNumber || $.t('app.notInformed'),
        },
      },
    ],
    textContent: model.serialNumber || '',
    input: InputType.TEXT,
    meta: {
      maxlength: '255',
    },
  },

  danfeAlt: {
    content: Helpers.formatScientificNumber(model.danfe),
  },

  danfe: {
    content: [
      {
        component: RenderCargaDanfe,
        props: {
          danfe: model.danfe,
        },
      },
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('danfe'),
          value: model.danfe,
        },
      },
      model.danfe,
    ],
    textContent: null,
    input: InputType.TEXT,
    meta: {
      required: true,
      selectall: true,
    },
  },

  tentativaAlt: {
    content: model.attempt || '',
    textContent: model.attempt || '',
  },

  tentativa: {
    content: [
      {
        component: RenderNumber,
        props: {
          number: model.attempt,
        },
      },
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('nroFotosOcorrencia'),
          value: model.attempt,
          tagClass: 'default',
        },
      },
    ],
    textContent: null,
  },

  endereco: {
    content: [
      model.endereco ? model.endereco.toString() : model.destinatario.endereco.toString(),
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('endereco'),
          value: model.endereco ? model.endereco.toString() : model.destinatario.endereco.toString()
            || $.t('app.notInformed'),
        },
      },
    ],
    textContent: model.endereco ? model.endereco.toString() : model.destinatario.endereco.toString(),
    input: InputType.SELECT,
  },

  latitudeEndereco: {
    content: [
      model.endereco ? $.filter.latitude(String(model.endereco.latitude)) :
        $.filter.latitude(String(model.destinatario.endereco.latitude)),
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('latitude'),
          value: model.endereco ? $.filter.latitude(String(model.endereco.latitude)) :
            $.filter.latitude(String(model.destinatario.endereco.latitude)),
        },
      },
    ],
    textContent: null,
  },

  longitudeEndereco: {
    content: [
      model.endereco ? $.filter.longitude(String(model.endereco.longitude)) :
        $.filter.longitude(String(model.destinatario.endereco.longitude)),
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('longitude'),
          value: model.endereco ? $.filter.longitude(String(model.endereco.longitude)) :
            $.filter.longitude(String(model.destinatario.endereco.longitude)),
        },
      },
    ],
    textContent: null,
  },

  dataCriacaoAlt: {
    content: datetime(model.dataCriacao),
    textContent: Helpers.datetimeForCsv(model.dataCriacao),
  },

  dataCriacao: {
    content: datetime(model.dataCriacao),
    textContent: null,
    input: InputType.DATETIME,
    meta: {
      selectall: true,
    },
  },

  dataInclusao: {
    content: datetime(model.dataCriacao),
    textContent: null,
    input: InputType.DATETIME,
    meta: {
      selectall: true,
    },
  },

  dataInicioEntregaAlt: {
    content: datetime(model.dataInicioEntrega),
    textContent: model.dataInicioEntrega &&
    model.sistemaCriador &&
    model.sistemaCriador.idSistemaPk !== 3 ?
      Helpers.datetimeForCsv(model.dataInicioEntrega) : '',
  },

  dataInicioEntrega: {
    content: [
      datetime(model.dataInicioEntrega),
      {
        component: RenderLabelValue,
        props: {
          label: model.isCargaConcreto ?
            model.translateColumn('dataInicioEntregaAlt') :
            model.translateColumn('dataInicioEntrega'),
          value: datetime(model.dataInicioEntrega) || '-',
        },
      },
    ],
    textContent: null,
    input: InputType.DATETIME,
  },

  dataCheckInAlt: {
    content: datetime(model.dataCheckIn),
    textContent: Helpers.datetimeForCsv(model.dataCheckIn),
  },

  dataCheckIn: {
    content: [
      datetime(model.dataCheckIn),
      {
        component: RenderLabelValue,
        props: {
          label: model.isCargaConcreto ?
            model.translateColumn('horarioChegadaObra') :
            model.translateColumn('dataCheckIn'),
          value: model.isCargaConcreto ?
            datetime(model.horarioChegadaObra) :
            datetime(model.dataCheckIn) || '-',
        },
      },
    ],
    textContent: null,
    input: InputType.DATETIME,
  },

  dataFinalizacao: {
    content: [
      datetime(model.dataFinalizacao),
      {
        component: RenderLabelValue,
        props: {
          label: model.isCargaConcreto ?
            model.translateColumn('horarioTerminoDescarga') :
            model.translateColumn('dataFinalizacao'),
          value: model.isCargaConcreto ?
            datetime(model.horarioTerminoDescarga) :
            datetime(model.dataFinalizacao) || '-',
        },
      },
    ],
    textContent: Helpers.datetimeForCsv(model.dataFinalizacao),
    input: InputType.DATETIME,
    meta: {
      selectall: true,
    },
  },

  dataPrevisaoAlt: {
    content: datetime(model.dataPrevisao),
    textContent: Helpers.datetimeForCsv(model.dataPrevisao),
  },

  dataPrevisao: {
    content: [
      datetime(model.dataPrevisao),
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('dataPrevisao'),
          value: datetime(model.dataPrevisao) || '-',
        },
      },
    ],
    textContent: null,
    input: InputType.DATETIME,
    meta: {
      selectall: true,
    },
  },

  dataBaixaSistemaAlt: {
    content: datetime(model.dataBaixaSistema),
    textContent: Helpers.datetimeForCsv(model.dataBaixaSistema),
  },

  dataBaixaSistema: {
    content: [
      datetime(model.dataBaixaSistema),
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('dataBaixaSistema'),
          value: datetime(model.dataBaixaSistema) || '-',
        },
      },
    ],
    textContent: null,
    editable: false,
    input: InputType.DATETIME,
    meta: {
      selectall: true,
    },
  },

  slaDias: {
    content: model.slaDias || '',
    textContent: model.slaDias || '',
  },

  slaDiasReal: {
    content: model.slaDiasReal || '',
    textContent: model.slaDiasReal || '',
  },

  slaStatusPendente: {
    content: model.slaPendente,
    textContent: model.slaPendente,
  },

  slaStatusFinalizado: {
    content: model.slaFinalizado,
    textContent: model.slaFinalizado,
  },

  slaStatus: {
    content: [
      {
        component: RenderLabelValue,
        props: {
          value: model.slaFinalizado,
          tagClass: model.slaTagClass,
        },
      },
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('slaStatus'),
          value: model.slaFinalizado,
          tagClass: model.slaTagClass,
        },
      },
    ],
    textContent: null,
    input: InputType.TEXT,
  },

  tempoCheckin: {
    content: [
      {
        component: RenderNumber,
        props: {
          number: model.checkinTime,
        },
      },
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('tempoCheckin'),
          value: model.checkinTime,
          tagClass: 'default',
        },
      },
    ],
    textContent: model.checkinTime,
  },

  tempoEntrega: {
    content: [
      {
        component: RenderNumber,
        props: {
          number: model.deliveryTime,
        },
      },
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('tempoEntrega'),
          value: model.deliveryTime,
          tagClass: 'default',
        },
      },
    ],
    textContent: model.deliveryTime,
  },

  statusCarga: {
    content: [
      {
        component: RenderLabelValue,
        props: {
          value: model.statusCarga && model.statusCarga.$tag || '-',
          tagClass: model.statusCargaTagClass,
        },
      },
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('statusCarga'),
          value: model.statusCarga && model.statusCarga.$tag || '-',
          tagClass: model.statusCargaTagClass,
        },
      },
    ],
    textContent: model.dataFinalizacao ?
      (model.tipoOcorrencia ?
        (model.devolvida ? 'Devolvida' : 'Com ocorrência') : 'Sem ocorrência') : '-',
    input: InputType.SELECT,
  },

  eventAlt: {
    textContent: model.tipoOcorrencia && model.tipoOcorrencia.$tag || '',
  },

  event: {
    content: [
      {
        component: RenderLabelValue,
        props: {
          value: model.eventTranslated,
          tagClass: model.eventTagClass,
        },
      },
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('event'),
          value: model.eventTranslated,
          tagClass: model.eventTagClass,
        },
      },
    ],
    textContent: null,
  },

  eventReason: {
    content: model.tipoOcorrencia && model.tipoOcorrencia.$tag || '',
    textContent: null,
  },

  tipoOcorrencia: {
    content: [
      {
        component: RenderLabelValue,
        props: {
          value: (model.tipoOcorrencia && model.tipoOcorrencia.$id && model.tipoOcorrencia.$tag) || '',
          tagClass: 'basic danger',
        },
      },
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('tipoOcorrencia'),
          value: (model.tipoOcorrencia && model.tipoOcorrencia.$id && model.tipoOcorrencia.$tag) || '',
          tagClass: 'basic danger',
        },
      },
    ],
    textContent: null,
    input: InputType.SELECT,
  },

  qtdPaleteEntregueAlt: {
    content: model.qtdPaleteEntregue !== null ? model.qtdPaleteEntregue : '',
    textContent: null,
  },

  qtdPaleteEntregue: {
    content: [
      model.qtdPaleteEntregue,
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('qtdPaleteEntregue'),
          value: model.qtdPaleteEntregue !== null ? model.qtdPaleteEntregue : $.t('app.notInformed'),
        },
      },
    ],
    textContent: model.qtdPaleteEntregue !== null ? model.qtdPaleteEntregue : '',
    input: InputType.MASK,
    meta: {
      mask: '######',
      selectall: true,
    },
  },

  qtdPaleteRetiradoAlt: {
    content: model.qtdPaleteRetirado !== null ? model.qtdPaleteRetirado : '',
    textContent: null,
  },

  qtdPaleteRetirado: {
    content: [
      model.qtdPaleteRetirado,
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('qtdPaleteRetirado'),
          value: model.qtdPaleteRetirado !== null ? model.qtdPaleteRetirado : $.t('app.notInformed'),
        },
      },
    ],
    textContent: model.qtdPaleteRetirado !== null ? model.qtdPaleteRetirado : '',
    input: InputType.MASK,
    meta: {
      mask: '######',
      selectall: true,
    },
  },

  observacao: {
    content: [
      model.observacao,
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('observacao'),
          value: model.observacao || '-',
        },
      },
    ],
    textContent: model.observacao || '',
    input: InputType.TEXT,
    meta: {
      maxlength: '255',
    },
  },

  codigoBarra: {
    content: [
      model.showCodigoBarras ? model.allCodigoBarrasInText : null,
      {
        component: RenderCargaBarcode,
        props: {
          items: model.showCodigoBarras ? model.codigoBarraList : null,
        },
      },
    ],
    textContent: model.allCodigoBarrasInText || '',
    input: InputType.TEXT,
    meta: {
      maxlength: '255',
    },
  },

  auditoriaLocalEntrega: {
    content: [
      {
        component: RenderLabelValue,
        props: {
          value: model.localAuditDelivery,
          tagClass: model.localAuditDeliveryTagClass,
        },
      },
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('auditoriaLocalEntrega'),
          value: model.localAuditDelivery,
          tagClass: model.localAuditDeliveryTagClass,
        },
      },
    ],
    textContent: model.localAuditDelivery,
  },

  latitudeEntregaAlt: {
    content: model.latitudeEntrega ? $.filter.latitude(String(model.latitudeEntrega)) : '-',
    textContent: model.latitudeEntrega ? $.filter.latitude(String(model.latitudeEntrega)) : '-',
  },

  latitudeEntrega: {
    content: [
      model.latitudeEntrega ? $.filter.latitude(String(model.latitudeEntrega)) : '-',
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('latitudeEntrega'),
          value: model.latitudeEntrega ? $.filter.latitude(String(model.latitudeEntrega)) : '-',
        },
      },
    ],
    textContent: null,
  },

  longitudeEntregaAlt: {
    content: model.longitudeEntrega ? $.filter.longitude(String(model.longitudeEntrega)) : '-',
    textContent: model.longitudeEntrega ? $.filter.longitude(String(model.longitudeEntrega)) : '-',
  },

  longitudeEntrega: {
    content: [
      model.longitudeEntrega ? $.filter.longitude(String(model.longitudeEntrega)) : '-',
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('longitudeEntrega'),
          value: model.longitudeEntrega ? $.filter.longitude(String(model.longitudeEntrega)) : '-',
        },
      },
    ],
    textContent: null,
  },

  mapLinkDelivery: {
    content: model.mapLinkDelivery,
    textContent: model.mapLinkDelivery,
  },

  auditoriaLocalCheckIn: {
    content: [
      {
        component: RenderLabelValue,
        props: {
          value: model.localAuditCheckIn,
          tagClass: model.localAuditCheckInTagClass,
        },
      },
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('auditoriaLocalCheckIn'),
          value: model.localAuditCheckIn,
          tagClass: model.localAuditCheckInTagClass,
        },
      },
    ],
    textContent: model.localAuditCheckIn,
  },

  latitudeCheckInAlt: {
    content: model.latitudeCheckIn ? $.filter.latitude(String(model.latitudeCheckIn)) : '-',
    textContent: model.latitudeCheckIn ? $.filter.latitude(String(model.latitudeCheckIn)) : '-',
  },

  latitudeCheckIn: {
    content: [
      model.latitudeCheckIn ? $.filter.latitude(String(model.latitudeCheckIn)) : '-',
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('latitudeCheckIn'),
          value: model.latitudeCheckIn ? $.filter.latitude(String(model.latitudeCheckIn)) : '-',
        },
      },
    ],
    textContent: null,
  },

  longitudeCheckInAlt: {
    content: model.longitudeCheckIn ? $.filter.longitude(String(model.longitudeCheckIn)) : '-',
    textContent: model.longitudeCheckIn ? $.filter.longitude(String(model.longitudeCheckIn)) : '-',
  },

  longitudeCheckIn: {
    content: [
      model.longitudeCheckIn ? $.filter.longitude(String(model.longitudeCheckIn)) : '-',
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('longitudeCheckIn'),
          value: model.longitudeCheckIn ? $.filter.longitude(String(model.longitudeCheckIn)) : '-',
        },
      },
    ],
    textContent: null,
  },

  mapLinkCheckIn: {
    textContent: model.mapLinkCheckIn,
  },

  motorista: {
    content: [
      model.motorista && model.motorista.nome || '',
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('motorista'),
          value: (model.motorista && model.motorista.nome) || $.t('app.notInformed'),
        },
      },
    ],
    textContent: model.motorista && model.motorista.$tag || '',
    input: InputType.SELECT,
  },

  motoristaCel: {
    content: phone(model.motoristaCel),
    textContent: null,
  },

  motoristaVersaoApp: {
    content: model.motorista && model.motorista.versao || '',
    textContent: model.motorista && model.motorista.versao || '',
  },

  motoristaMarca: {
    content: model.motorista && model.motorista.marcaAparelho || '',
    textContent: model.motorista && model.motorista.marcaAparelho || '',
  },

  motoristaModelo: {
    content: model.motorista && model.motorista.modeloAparelho || '',
    textContent: model.motorista && model.motorista.modeloAparelho || '',
  },

  nomeRecebedorAlt: {
    content: model.nomeRecebedor || '',
    textContent: model.nomeRecebedor || '',
  },

  nomeRecebedor: {
    content: [
      model.nomeRecebedor,
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('nomeRecebedor'),
          value: model.renderNomeRecebedor,
        },
      },
    ],
    textContent: null,
    input: InputType.TEXT,
    meta: {
      maxlength: '255',
    },
  },

  placaVeiculoAlt: {
    content: model.motorista.placaVeiculo || '',
    textContent: model.motorista.placaVeiculo || '',
  },

   placaVeiculo: {
    content: [
      model.placaVeiculo,
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('placaVeiculo'),
          value: (model.motorista && model.motorista.placaVeiculo) || model.placaVeiculo || $.t('app.notInformed'),
        },
      },
    ],
     textContent: null,
    input: InputType.TEXT,
    meta: {
      maxlength: '255',
    },
  },

  tipoVeiculoAlt: {
    content: model.motorista.tipoVeiculo || '',
    textContent: model.motorista.tipoVeiculo || '',
  },

  tipoVeiculo: {
    content: [
      model.tipoVeiculo,
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('tipoVeiculo'),
          value: (model.motorista && model.motorista.tipoVeiculo) || model.tipoVeiculo || $.t('app.notInformed'),
        },
      },
    ],
    textContent: null,
    input: InputType.TEXT,
    meta: {
      maxlength: '255',
    },
  },

  documentoRecebedorAlt: {
    content: $.filter.cpfOrCnpj(model.documentoRecebedor || ''),
    textContent: $.filter.cpfOrCnpj(model.documentoRecebedor || ''),
  },

  documentoRecebedor: {
    content: [
      $.filter.cpfOrCnpj(model.documentoRecebedor || ''),
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('documentoRecebedor'),
          value: model.renderDocumentoRecebedor,
        },
      },
    ],
    textContent: null,
    input: InputType.CPF_OR_CNPJ,
    meta: {
      maxlength: '255',
    },
  },

  rgRecebedorAlt: {
    content: model.rgRecebedor || '',
    textContent: model.rgRecebedor || '',
  },

  rgRecebedor: {
    content: [
      model.rgRecebedor || $.t('app.notInformed'),
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('rgRecebedor'),
          value: model.renderRgRecebedor,
        },
      },
    ],
    textContent: null,
    input: InputType.RG,
    meta: {
      maxlength: '255',
    },
  },

  finalizadoViaAlt: {
    content: model.statusCarga && model.statusCarga.$id === DeliveryStatus.FINISHED ?
      model.sistemaFinalizador && model.sistemaFinalizador.$tag : '',
    textContent: model.statusCarga && model.statusCarga.$id === DeliveryStatus.FINISHED ?
      model.sistemaFinalizador && model.sistemaFinalizador.$tag : '',
  },

  finalizadoVia: {
    content: [
      model.sistemaFinalizador && model.sistemaFinalizador.$tag,
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('finalizadoVia'),
          value: model.statusCarga && model.statusCarga.$id === DeliveryStatus.FINISHED ?
            model.sistemaFinalizador && model.sistemaFinalizador.$tag : null,
        },
      },
    ],
    textContent: null,
    input: InputType.CHECKBOX,
  },

  criadoPorNomeRazaoSocial: {
    content: [
      model.responsavelCriador && model.responsavelCriador.razaoSocial
      || model.motoristaCriador && model.motoristaCriador.nome,
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('criadoPorNomeRazaoSocial'),
          value: model.responsavelCriador && model.responsavelCriador.razaoSocial
            || model.motoristaCriador && model.motoristaCriador.nome
            || '-',
        },
      },
    ],
    textContent: null,
    input: InputType.CHECKBOX,
  },

  criadoPorCpfCnpj: {
    content: model.responsavelCriador && model.responsavelCriador.cnpj ? cpfOrCnpj(model.responsavelCriador.cnpj)
        : (model.motoristaCriador && model.motoristaCriador.cpf ? cpfOrCnpj(model.motoristaCriador.cpf) : ''),
    textContent: model.responsavelCriador && model.responsavelCriador.cnpj ? cpfOrCnpj(model.responsavelCriador.cnpj)
        : (model.motoristaCriador && model.motoristaCriador.cpf ? cpfOrCnpj(model.motoristaCriador.cpf) : ''),
  },

  criadoVia: {
    content: [
      model.sistemaCriador && model.sistemaCriador.$tag,
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('criadoVia'),
          value: model.sistemaCriador && model.sistemaCriador.$tag || $.t('app.notInformed'),
        },
      },
    ],
    textContent: model.sistemaCriador && model.sistemaCriador.$tag,
    input: InputType.CHECKBOX,
  },

  auditoriaStatus: {
    content: model.auditoriaStatus,
    textContent: model.auditoriaStatus,
  },

  auditoriaDataCriacao: {
    content: model.auditoria && model.auditoria.dataCriacao ? datetime(model.auditoria.dataCriacao) : '',
    textContent: model.auditoria && model.auditoria.dataCriacao ? datetime(model.auditoria.dataCriacao) : '',
  },

  auditoriaResponsavel: {
    content: model.auditoria && model.auditoria.responsavel && model.auditoria.responsavel.$tag || '',
    textContent: model.auditoria && model.auditoria.responsavel && model.auditoria.responsavel.$tag || '',
  },

  auditoriaMotivo: {
    content: model.auditoria && model.auditoria.motivo && model.auditoria.motivo.$tag || '',
    textContent: model.auditoria && model.auditoria.motivo && model.auditoria.motivo.$tag || '',
  },

  auditoriaObservacao: {
    content: model.auditoria && model.auditoria.observacoesAsText,
    textContent: model.auditoria && model.auditoria.observacoesAsText,
  },

  destinatario: {
    content: model.destinatario && model.destinatario.$tag,
    textContent: null,
  },

  destinatarioRazaoSocial: {
    content: model.destinatario && model.destinatario.$tag || '',
    textContent: model.destinatario && model.destinatario.$tag || '',
  },

  destinatarioCpfCnpj: {
    content: model.destinatario && model.destinatario.cpfCnpj ? cpfOrCnpj(model.destinatario.cpfCnpj) : '',
    textContent: model.destinatario && model.destinatario.cpfCnpj ? cpfOrCnpj(model.destinatario.cpfCnpj) : '',
  },

  destinatarioTelefone: {
    content: model.destinatario && model.destinatario.telefone ? phone(model.destinatario.telefone) : '',
    textContent: model.destinatario && model.destinatario.telefone ? phone(model.destinatario.telefone) : '',
  },

  destinatarioTelefoneAlt: {
    content: model.destinatario && model.destinatario.telefone ? phone(model.destinatario.telefone) : '',
    textContent: null,
  },

  destinatarioBairro: {
    content: model.destinatario && model.destinatario.endereco && model.destinatario.endereco.bairro || '',
    textContent: model.destinatario && model.destinatario.endereco && model.destinatario.endereco.bairro || '',
  },

  destinatarioMunicipio: {
    content: model.destinatario && model.destinatario.endereco && model.destinatario.endereco.cidade || '',
    textContent: model.destinatario && model.destinatario.endereco && model.destinatario.endereco.cidade || '',
  },

  destinatarioUf: {
    content: model.destinatario && model.destinatario.endereco && model.destinatario.endereco.uf || '',
    textContent: model.destinatario && model.destinatario.endereco && model.destinatario.endereco.uf || '',
  },

  destinatarioCep: {
    content: model.destinatario && model.destinatario.endereco && model.destinatario.endereco.cep ?
      cep(model.destinatario.endereco.cep) : '',
    textContent: model.destinatario && model.destinatario.endereco && model.destinatario.endereco.cep ?
      cep(model.destinatario.endereco.cep) : '',
  },

  destinatarioIndicadorIe: {
    content: model.destinatario && model.destinatario.indicadorIe || '',
    textContent: model.destinatario && model.destinatario.indicadorIe || '',
  },

  destinatarioIm: {
    content: model.destinatario && model.destinatario.im || '',
    textContent: model.destinatario && model.destinatario.im || '',
  },

  destinatarioInscricaoEstadual: {
    content: model.destinatario && model.destinatario.inscricaoEstadual || '',
    textContent: model.destinatario && model.destinatario.inscricaoEstadual || '',
  },

  destinatarioInscricaoSuframa: {
    content: model.destinatario && model.destinatario.inscricaoSuframa || '',
    textContent: model.destinatario && model.destinatario.inscricaoSuframa || '',
  },

  destinatarioEmail: {
    content: model.destinatario && model.destinatario.email || '',
    textContent: model.destinatario && model.destinatario.email || '',
  },

  destinatarioEndereco: {
    content: model.destinatario && model.destinatario.endereco && model.destinatario.endereco.toString(),
    textContent: model.destinatario && model.destinatario.endereco && model.destinatario.endereco.toString(),
  },

  transportadorEnd: {
    content: model.transportador && model.transportador.responsavel &&
      model.transportador.responsavel.endereco.toString(),
    textContent: model.transportador && model.transportador.responsavel &&
      model.transportador.responsavel.endereco.toString(),
  },

  transportador: {
    content: model.transportador && model.transportador.responsavel && model.transportador.responsavel.$tag
      || '',
    textContent: null,
  },

  transportadorRazaoSocial: {
    content: model.transportador && model.transportador.responsavel && model.transportador.responsavel.razaoSocial
      || '',
    textContent: model.transportador && model.transportador.responsavel && model.transportador.responsavel.razaoSocial
      || '',
  },

  transportadorCpfCnpj: {
    content: model.transportador && model.transportador.responsavel && model.transportador.responsavel.cnpj ?
      cnpj(model.transportador.responsavel.cnpj) : '',
    textContent: model.transportador && model.transportador.responsavel && model.transportador.responsavel.cnpj ?
      cnpj(model.transportador.responsavel.cnpj) : '',
  },

  transportadorTelefone: {
    content: model.transportador && model.transportador.responsavel && model.transportador.responsavel.telefone
      || '',
    textContent: model.transportador && model.transportador.responsavel && model.transportador.responsavel.telefone
      || '',
  },

  transportadorBairro: {
    content: model.transportador &&
      model.transportador.responsavel &&
      model.transportador.responsavel &&
      model.transportador.responsavel.endereco &&
      model.transportador.responsavel.endereco.bairro || '',
    textContent: model.transportador &&
      model.transportador.endereco &&
      model.transportador.endereco.bairro || '',
  },

  transportadorMunicipio: {
    content: model.transportador &&
      model.transportador.responsavel &&
      model.transportador.responsavel &&
      model.transportador.responsavel.endereco &&
      model.transportador.responsavel.endereco.cidade || '',
    textContent: model.transportador &&
      model.transportador.endereco &&
      model.transportador.endereco.cidade || '',
  },

  transportadorUf: {
    content: model.transportador &&
      model.transportador.responsavel &&
      model.transportador.responsavel &&
      model.transportador.responsavel.endereco &&
      model.transportador.responsavel.endereco.uf || '',
    textContent: model.transportador &&
      model.transportador.endereco &&
      model.transportador.endereco.uf || '',
  },

  transportadorCep: {
    content: model.transportador &&
    model.transportador.responsavel &&
    model.transportador.responsavel &&
    model.transportador.responsavel.endereco &&
    model.transportador.responsavel.endereco.cep ? cep(model.transportador.responsavel.endereco.cep) : '',
    textContent: model.transportador &&
    model.transportador.endereco &&
    model.transportador.endereco.cep ? cep(model.transportador.endereco.cep) : '',
  },

  embarcador: {
    content: model.embarcador &&
      model.embarcador.responsavel &&
      model.embarcador.responsavel.$tag || '',
    textContent: null,
  },

  embarcadorRazaoSocialAlt: {
    content: model.embarcador &&
      model.embarcador.responsavel &&
      model.embarcador.responsavel.razaoSocial || '',
    textContent: null,
  },

  embarcadorRazaoSocial: {
    content: model.embarcador &&
      model.embarcador.responsavel &&
      model.embarcador.responsavel.razaoSocial || '',
    textContent: model.embarcador &&
      model.embarcador.responsavel &&
      model.embarcador.responsavel.razaoSocial || '',
  },

  embarcadorNomeFantasia: {
    content: model.embarcador &&
      model.embarcador.responsavel &&
      model.embarcador.responsavel.nomeFantasia || '',
    textContent: model.embarcador &&
      model.embarcador.responsavel &&
      model.embarcador.responsavel.nomeFantasia || '',
  },

  embarcadorCpfCnpjAlt: {
    content: model.embarcador &&
    model.embarcador.responsavel &&
    model.embarcador.responsavel.cnpj ? cpfOrCnpj(model.embarcador.responsavel.cnpj) : '',
    textContent: null,
  },

  embarcadorCpfCnpj: {
    content: model.embarcador &&
    model.embarcador.responsavel &&
    model.embarcador.responsavel.cnpj ? cpfOrCnpj(model.embarcador.responsavel.cnpj) : '',
    textContent: model.embarcador &&
    model.embarcador.responsavel &&
    model.embarcador.responsavel.cnpj ? cpfOrCnpj(model.embarcador.responsavel.cnpj) : '',
  },

  embarcadorTelefone: {
    content: model.embarcador &&
    model.embarcador.responsavel &&
    model.embarcador.responsavel.telefone ? phone(model.embarcador.responsavel.telefone) : '',
    textContent: model.embarcador &&
    model.embarcador.responsavel &&
    model.embarcador.responsavel.telefone ? phone(model.embarcador.responsavel.telefone) : '',
  },

  embarcadorBairro: {
    content: model.embarcador &&
      model.embarcador.responsavel &&
      model.embarcador.responsavel.endereco &&
      model.embarcador.responsavel.endereco.bairro || '',
    textContent: model.embarcador &&
      model.embarcador.endereco &&
      model.embarcador.endereco.bairro || '',
  },

  embarcadorMunicipio: {
    content: model.embarcador &&
      model.embarcador.responsavel &&
      model.embarcador.responsavel.endereco &&
      model.embarcador.responsavel.endereco.cidade || '',
    textContent: model.embarcador &&
      model.embarcador.endereco &&
      model.embarcador.endereco.cidade || '',
  },

  embarcadorUf: {
    content: model.embarcador &&
      model.embarcador.responsavel &&
      model.embarcador.responsavel.endereco &&
      model.embarcador.responsavel.endereco.uf || '',
    textContent: model.embarcador &&
      model.embarcador.endereco &&
      model.embarcador.endereco.uf || '',
  },

  embarcadorCep: {
    content: model.embarcador &&
    model.embarcador.responsavel &&
    model.embarcador.responsavel.endereco &&
    model.embarcador.responsavel.endereco.uf ? cep(model.embarcador.responsavel.endereco.uf) : '',
    textContent: model.embarcador &&
    model.embarcador.endereco &&
    model.embarcador.endereco.cep ? cep(model.embarcador.endereco.cep) : '',
  },

  embarcadorInscricaoEstadual: {
    content: model.embarcador && model.embarcador.inscricaoEstadual || '',
    textContent: model.embarcador && model.embarcador.inscricaoEstadual || '',
  },

  embarcadorInscricaoMunicipal: {
    content: model.embarcador && model.embarcador.inscricaoMunicipal || '',
    textContent: model.embarcador && model.embarcador.inscricaoMunicipal || '',
  },

  embarcadorCnaeFiscal: {
    content: model.embarcador && model.embarcador.cnaeFiscal || '',
    textContent: model.embarcador && model.embarcador.cnaeFiscal || '',
  },

  embarcadorInscricaoEstadualTributario: {
    content: model.embarcador && model.embarcador.substitutoTributario || '',
    textContent: model.embarcador && model.embarcador.substitutoTributario || '',
  },

  embarcadorMunicipioIcms: {
    content: model.embarcador && model.embarcador.municipioIcms || '',
    textContent: model.embarcador && model.embarcador.municipioIcms || '',
  },

  embarcadorEndereco: {
    content: model.embarcador &&
      model.embarcador.responsavel &&
      model.embarcador.responsavel.endereco.toString(),
    textContent: model.embarcador &&
      model.embarcador.responsavel &&
      model.embarcador.responsavel.endereco.toString(),
  },

  devolvida: {
    content: bool(model.devolvida || false),
    textContent: bool(model.devolvida || false),
    input: InputType.CHECKBOX,
  },

  nroFotosOcorrenciaAlt: {
    content: model.nroFotosOcorrencia || '0',
    textContent: model.nroFotosOcorrencia || '0',
  },

  nroFotosOcorrencia: {
    content: [
      {
        component: RenderNumber,
        props: {
          number: model.nroFotosOcorrencia || 0,
        },
      },
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('nroFotosOcorrencia'),
          value: model.nroFotosOcorrencia || 0,
          tagClass: 'default',
        },
      },
    ],
    textContent: null,
  },

  nroComprovantesAlt: {
    content: model.nroComprovantes || '0',
    textContent: model.nroComprovantes || '0',
  },

  nroComprovantes: {
    content: [
      {
        component: RenderNumber,
        props: {
          number: model.nroComprovantes || 0,
        },
      },
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('nroFotosOcorrencia'),
          value: model.nroComprovantes || 0,
          tagClass: 'default',
        },
      },
    ],
    textContent: null,
  },

  compartilhar: {
    content: model.urlVisitante || '',
    textContent: model.urlVisitante || '',
  },

  mesEntrega: {
    content: model.dataFinalizacao ? moment(model.dataFinalizacao).format('MM') : '',
    textContent: model.dataFinalizacao ? moment(model.dataFinalizacao).format('MM') : '',
  },

  sigla: {
    content: model.sigla || '',
    textContent: model.sigla || '',
    input: InputType.TEXT,
    meta: {
      maxlength: '255',
    },
  },

  siglaCielo: {
    content: [
      {
        component: RenderLabelValue,
        props: {
          label: 'Código do Serviço',
          value: model.sigla || '',
        },
      },
    ],
    textContent: null,
  },

  nomeMotorista: {
    content: [
      model.nomeMotorista || '',
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('nomeMotorista'),
          value: (model.motorista && model.motorista.$tag) || model.nomeMotorista || $.t('app.notInformed'),
        },
      },
    ],
    textContent: null,
    input: InputType.TEXT,
    meta: {
      maxlength: '255',
    },
  },

  xpedido: {
    content: model.xpedido || '',
    textContent: model.xpedido || '',
    input: InputType.MASK,
    meta: {
      mask: '##########',
    },
  },

    xpedidoConcreserv: {
        content: [
            {
                component: RenderLabelValue,
                props: {
                    label: 'Lacre',
                    value: model.xpedido || '',
                },
            },
        ],
      textContent: null,
    },

  contrato: {
    textContent: null,
    content: [
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('contrato'),
          value: model.cargaConcreto ? model.cargaConcreto.contrato : $.t('app.notInformed'),
        },
      },
    ],
  },

  responsavel: {
    content: model.responsavel && model.responsavel.$tag,
    textContent: null,
    input: InputType.SELECT,
  },

  cpfCnpjDestinatario: {
    content: cpfOrCnpj(model.destinatario && model.destinatario.cpfCnpj),
    textContent: null,
    input: InputType.CPF_OR_CNPJ,
  },

  nomeDestinatario: {
    content: model.destinatario && model.destinatario.nome ? model.destinatario.nome : '',
    textContent: null,
  },

  outroResponsavel: {
    content: model.responsavel && model.responsavel.$id,
    textContent: null,
    input: InputType.TEXT,
    hidden: true,
  },

  ativo: {
    content: bool(model.ativo),
    hidden: true,
    editable: false,
  },

  idCargaApp: {
    content: model.idCargaApp,
    input: InputType.TEXT,
    hidden: true,
    meta: {
      maxlength: '255',
    },
  },

  criadoViaPainel: {
    content: bool(model.criadoViaPainel),
    input: InputType.CHECKBOX,
    hidden: true,
  },

  cte: {
    content: [
      model.cte,
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('cte'),
          value: model.cte || '-',
        },
      },
    ],
    textContent: null,
  },

  nroCte: {
    content: [
      model.nroCte,
      {
        component: RenderLabelValue,
        props: {
          label: model.isCargaConcreto ? model.translateColumn('pedido') : model.translateColumn('nroCte'),
          value: model.nroCte || '-',
        },
      },
    ],
    textContent: null,
  },

  nroCteAlt: {
    content: model.cargaInfo && model.cargaInfo.nroCte ? model.cargaInfo.nroCte : '',
    textContent: null,
    input: InputType.TEXT,
  },

  outroDocumentoRecebedor: {
    content: [
      model.outroDocumentoRecebedor,
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('outroDocumentoRecebedor'),
          value: model.outroDocumentoRecebedor || $.t('app.notInformed'),
        },
      },
    ],
    textContent: null,
  },

  distanciaParaDestinoEmMetros: {
    content: [
      model.distanciaParaDestinoEmMetros,
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('distanciaParaDestinoEmMetros'),
          value: model.distanciaParaDestinoEmMetrosFormatted,
        },
      },
    ],
    textContent: null,
  },

  duracaoParaDestinoEmSegundos: {
    content: [
      model.duracaoParaDestinoEmSegundos,
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('duracaoParaDestinoEmSegundos'),
          value: model.ultimaDuracaoParaDestinoEmSegundosFormatted,
        },
      },
    ],
    textContent: null,
  },

  protocolSsw: {
    content: model.protocolSsw,
    input: InputType.TEXT,
    hidden: true,
    meta: {
      maxlength: '255',
    },
  },

  dataRemocao: {
    content: datetime(model.dataRemocao),
    input: InputType.DATETIME,
    hidden: true,
  },

  distanciaCheckInEmMetros: {
    content: model.distanciaCheckInEmMetros,
    input: InputType.NUMBER,
    hidden: true,
    meta: {
      step: 'any',
    },
  },

  distanciaEntregaEmMetros: {
    content: model.distanciaEntregaEmMetros,
    input: InputType.NUMBER,
    hidden: true,
    meta: {
      step: 'any',
    },
  },

  reprocessarRedesul: {
    content: bool(model.reprocessarRedesul),
    input: InputType.CHECKBOX,
    hidden: true,
  },

  pontoReferenciaEntrega: {
    content: [
      model.pontoReferenciaEntrega,
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('pontoReferenciaEntrega'),
          value: model.pontoReferenciaEntrega || '-',
        },
      },
    ],
    textContent: model.pontoReferenciaEntrega || '',
    input: InputType.TEXT,
    meta: {
      maxlength: '255',
    },
  },

  tipoPessoaRecebedor: {
    content: {
      component: RenderLabelValue,
      props: {
        label: model.translateColumn('tipoPessoaRecebedor'),
        value: model.tipoPessoaRecebedor &&
        model.tipoPessoaRecebedor.$tag ? model.tipoPessoaRecebedor.$tag : $.t('app.notInformed'),
      },
    },
    textContent: null,
  },

  tipoPessoaRecebedorAlt: {
    content: model.tipoPessoaRecebedor &&
    model.tipoPessoaRecebedor.$tag ? model.tipoPessoaRecebedor.$tag : '',
  },

  auditoriaDataCanhoto: {
    content: model.auditoria && model.auditoria.dataCanhoto ? datetimeForCsv(model.auditoria.dataCanhoto) : '',
    textContent: model.auditoria && model.auditoria.dataCanhoto ? datetimeForCsv(model.auditoria.dataCanhoto) : '',
  },

  dataEmissaoDanfeAlt: {
    content: datetime(model.dataEmissaoDanfe),
    textContent: Helpers.datetimeForCsv(model.dataEmissaoDanfe),
  },

  dataEmissaoDanfe: {
    content: [
      datetime(model.dataEmissaoDanfe),
      {
        component: RenderLabelValue,
        props: {
          label: model.translateColumn('dataEmissaoDanfe'),
          value: datetime(model.dataEmissaoDanfe) || '-',
        },
      },
    ],
    textContent: null,
    input: InputType.DATETIME,
    meta: {
      selectall: true,
    },
  },

  centroDeCusto: {
    content: model.centroDeCusto && model.centroDeCusto.nome,
    textContent: model.centroDeCusto && model.centroDeCusto.nome,
  },
  horarioSaidaObra: {
    textContent: null,
    content: {
      component: RenderLabelValue,
      props: {
        label: model.translateColumn('horarioSaidaObra'),
        value: datetime(model.horarioSaidaObra) || '-',
      },
    },
  },
  horarioChegadaCentral: {
    textContent: null,
    content: {
      component: RenderLabelValue,
      props: {
        label: model.translateColumn('horarioChegadaCentral'),
        value: datetime(model.horarioChegadaCentral) || '-',
      },
    },
  },
  horarioInicioDescarga: {
    textContent: null,
    content: {
      component: RenderLabelValue,
      props: {
        label: model.translateColumn('horarioInicioDescarga'),
        value: datetime(model.horarioInicioDescarga) || '-',
      },
    },
  },

  manifesto: {
    content: model.cargaInfo ? model.cargaInfo.manifesto : '',
  },
})
